/**
 * Campaign-aware API binding.
 *
 * Each campaign targets its own backend module (separate endpoints), so the
 * generated react-query hooks differ per campaign. This module exposes a single
 * `campaignApi` object pointing to the hooks of the active campaign, so the form
 * components stay campaign-agnostic.
 */
import type { UseMutationOptions, UseMutationResult } from '@tanstack/react-query'
import {
  useBasketballHoopControllerCreateEmailConfirmation,
  useBasketballHoopControllerCreateOwnerChanged,
  useBasketballHoopControllerCreateUnknownNewOwner,
  useBasketballHoopControllerCreateCrankQuantity,
  useBasketballHoopControllerCreateKitRequest,
  useSd500Emd500eControllerCreateEmailConfirmation,
  useSd500Emd500eControllerCreateOwnerChanged,
  useSd500Emd500eControllerCreateUnknownNewOwner,
  useSd500Emd500eControllerCreateProductQuantity,
  useSd500Emd500eControllerCreateKitRequest,
} from '../api/generated'
import { activeCampaignSlug } from './active'

/**
 * Loose mutation hook signature shared by every campaign.
 * Payloads differ slightly between products (quantity shape), so `data` is kept
 * generic; each form builds its own typed payload locally.
 */
export type CampaignMutationHook = (options?: {
  mutation?: UseMutationOptions<any, any, { data: any }, any>
}) => UseMutationResult<any, any, { data: any }, any>

/**
 * Set of mutation hooks used across the recall wizard for a single campaign.
 */
export interface CampaignApi {
  useCreateEmailConfirmation: CampaignMutationHook
  useCreateOwnerChanged: CampaignMutationHook
  useCreateUnknownNewOwner: CampaignMutationHook
  useCreateQuantity: CampaignMutationHook
  useCreateKitRequest: CampaignMutationHook
}

const apiByCampaign: Record<string, CampaignApi> = {
  'b900-box': {
    useCreateEmailConfirmation:
      useBasketballHoopControllerCreateEmailConfirmation as CampaignMutationHook,
    useCreateOwnerChanged: useBasketballHoopControllerCreateOwnerChanged as CampaignMutationHook,
    useCreateUnknownNewOwner:
      useBasketballHoopControllerCreateUnknownNewOwner as CampaignMutationHook,
    useCreateQuantity: useBasketballHoopControllerCreateCrankQuantity as CampaignMutationHook,
    useCreateKitRequest: useBasketballHoopControllerCreateKitRequest as CampaignMutationHook,
  },
  'sd500-emd500e': {
    useCreateEmailConfirmation:
      useSd500Emd500eControllerCreateEmailConfirmation as CampaignMutationHook,
    useCreateOwnerChanged: useSd500Emd500eControllerCreateOwnerChanged as CampaignMutationHook,
    useCreateUnknownNewOwner:
      useSd500Emd500eControllerCreateUnknownNewOwner as CampaignMutationHook,
    useCreateQuantity: useSd500Emd500eControllerCreateProductQuantity as CampaignMutationHook,
    useCreateKitRequest: useSd500Emd500eControllerCreateKitRequest as CampaignMutationHook,
  },
}

/**
 * API hooks for the active campaign.
 *
 * Fails fast when CAMPAIGN is missing or unknown, for the same reason as the
 * campaign registry: no silent fallback to a default campaign.
 */
if (!activeCampaignSlug || !apiByCampaign[activeCampaignSlug]) {
  throw new Error(
    `CAMPAIGN is required and must be one of: ${Object.keys(apiByCampaign).join(', ')} (got "${
      activeCampaignSlug ?? 'undefined'
    }")`,
  )
}

export const campaignApi: CampaignApi = apiByCampaign[activeCampaignSlug]
